/**
 * Port of lib/photo_traits.py.
 *
 * Photo → appearance traits.
 *
 * A photo answers most of the appearance questions at a glance (build, complexion, face shape,
 * standout features, hair), so this lets the user upload an image instead of describing the person.
 * The vision model does pure EXTRACTION onto the SAME structured trait options the questionnaire uses
 * (lib/traits TRAIT_QUESTIONS) — it never ranks candidates; the deterministic engine still does all the
 * astrology. The caller is expected to PREVIEW the extracted answers and only merge them on confirm.
 *
 * extract_traits_from_photo is async (openai_service is fetch-based). Image bytes are a
 * Uint8Array (browser: from File.arrayBuffer()); base64 is hand-rolled so the module works in
 * both browser and Node without dependencies.
 */

import { call_openai_vision_json } from "./openai_service";
import { load_instruction, load_schema } from "./instructions";
import { pythonRound } from "./pyutil";
import { SIGN_NAMES, normalize_trait_answers, trait_questions_for_age, NON_VISUAL_QUESTION_KEYS, type TraitQuestion } from "./traits";

// Test seam replacing Python's monkeypatch of the module-level import.
type VisionCall = typeof call_openai_vision_json;
let _call_openai_vision_json: VisionCall = call_openai_vision_json;

export function set_call_openai_vision_json_impl(impl: VisionCall | null): void {
  _call_openai_vision_json = impl ?? call_openai_vision_json;
}

/**
 * The questions a photo may fill: VISIBLE PHYSICAL characteristics only — never the personality
 * dimensions or voice (a photo cannot show disposition or sound, and guessing them would be
 * unfounded) — and only the options appropriate for the person's age (no early-receding hairline
 * for a child, no growth-stage height/build for a young child).
 */
function _visual_questions(age_years: number | null = null): TraitQuestion[] {
  return trait_questions_for_age(age_years).filter((q) => !NON_VISUAL_QUESTION_KEYS.has(q.key));
}

export function _option_catalog(age_years: number | null = null): Record<string, any>[] {
  return _visual_questions(age_years).map((q) => ({
    key: q.key,
    asks_about: q.prompt,
    options: Object.fromEntries(Object.entries(q.options).map(([k, o]) => [k, o.label])),
  }));
}

/**
 * traits (every VISUAL question → array of valid option keys), rejected_traits (the options each
 * VISUAL question clearly CONTRADICTS — e.g. a clearly lean build rejects stocky/large), a
 * rising-sign likelihood, and short observations describing what was actually visible (so the
 * preview is auditable). Temperament is deliberately absent — a photo only reads physical features.
 */
export function _photo_trait_schema(age_years: number | null = null): Record<string, any> {
  // Both `traits` and `rejected_traits` cover the same VISUAL questions, so one injected prop set
  // fills both `$inject:trait_options`/`$inject:trait_keys` markers in trait_photo.json. The
  // age-gated options and sign list are injected so the editable JSON tracks the live trait bank.
  const trait_props: Record<string, any> = {};
  for (const q of _visual_questions(age_years)) {
    trait_props[q.key] = { type: "array", items: { type: "string", enum: Object.keys(q.options) } };
  }
  return load_schema("trait_photo.json", {
    trait_options: trait_props,
    trait_keys: Object.keys(trait_props),
    sign_names: [...SIGN_NAMES],
  });
}

/** The photo-vision system prompt — loaded from instructions/trait_photo.md (editable). */
export const _SYSTEM_PROMPT = load_instruction("trait_photo.md");

const _B64_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

/** base64.b64encode for a byte array — dependency-free, browser + Node. */
function _b64encode(bytes: Uint8Array): string {
  let out = "";
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i];
    const b1 = i + 1 < bytes.length ? bytes[i + 1] : 0;
    const b2 = i + 2 < bytes.length ? bytes[i + 2] : 0;
    out += _B64_ALPHABET[b0 >> 2];
    out += _B64_ALPHABET[((b0 & 0x03) << 4) | (b1 >> 4)];
    out += i + 1 < bytes.length ? _B64_ALPHABET[((b1 & 0x0f) << 2) | (b2 >> 6)] : "=";
    out += i + 2 < bytes.length ? _B64_ALPHABET[b2 & 0x3f] : "=";
  }
  return out;
}

export function image_bytes_to_data_url(image_bytes: Uint8Array, mime_type = "image/jpeg"): string {
  const b64 = _b64encode(image_bytes);
  return `data:${mime_type};base64,${b64}`;
}

/**
 * Analyse a photo and return {traits, likelihood, observations, error}. `traits` is normalized to
 * the engine's option keys; `likelihood` is {sign_name: 0-100}. Nothing is saved here — the caller
 * previews and confirms. Pass age_years (from the birth date) so age-inappropriate options are
 * excluded from the schema and the model is told the person's real age.
 */
export async function extract_traits_from_photo(
  image_bytes: Uint8Array | null | undefined,
  mime_type = "image/jpeg",
  run_data: Record<string, any> | null = null,
  log_slug: any = null,
  age_years: number | null = null,
): Promise<Record<string, any>> {
  if (!image_bytes || image_bytes.length === 0) {
    return { traits: {}, rejected: {}, likelihood: {}, observations: [], error: "No image provided." };
  }
  const data_url = image_bytes_to_data_url(image_bytes, mime_type || "image/jpeg");
  const age_note =
    age_years !== null && age_years !== undefined
      ? `\n\nThe person's actual age is about ${pythonRound(age_years, 0).toFixed(0)} years — judge all features against that age group.`
      : "";
  const user_text =
    "Extract this person's appearance onto the allowed options. VALID TRAIT OPTIONS (use only these " +
    "exact keys):\n" +
    JSON.stringify(_option_catalog(age_years)) +
    "\n\nAVAILABLE RISING SIGNS: " +
    SIGN_NAMES.join(", ") +
    age_note;
  const [parsed, _response_id, error] = await _call_openai_vision_json(
    "photo_traits",
    _photo_trait_schema(age_years),
    _SYSTEM_PROMPT,
    user_text,
    [data_url],
    log_slug,
    run_data,
  );
  if (error || !parsed) {
    return { traits: {}, rejected: {}, likelihood: {}, observations: [], error: error || "No response from the vision model." };
  }
  const likelihood: Record<string, number> = {};
  for (const item of parsed["rising_sign_likelihood"] || []) {
    const sign = item?.["sign"];
    if (SIGN_NAMES.includes(sign)) {
      const raw = item?.["score"];
      const score = Number(raw);
      if (raw === null || raw === undefined || Number.isNaN(score)) continue;
      likelihood[sign] = Math.max(0.0, Math.min(100.0, score));
    }
  }
  const observations = (parsed["observations"] || []).map((o: any) => String(o).trim()).filter((o: string) => o);
  const traits = normalize_trait_answers(parsed["traits"] || {}, age_years);
  const rejected = normalize_trait_answers(parsed["rejected_traits"] || {}, age_years);
  // A photo never sets personality/voice, even if the model slips one in.
  for (const k of NON_VISUAL_QUESTION_KEYS) {
    delete traits[k];
    delete rejected[k];
  }
  // An option can't be both present and clearly absent in the same photo. The app-wide
  // convention (PLAN.md rejected-traits design; traits._delivery_auto_rejections: "asserted AND
  // rejected → rejection wins") is REJECTION-WINS on contradiction — a rejection is the stronger,
  // deliberate claim ("clearly contradicted"), while a positive can be a stereotyped over-read.
  // So a contradictory option is stripped out of the POSITIVES and kept as a rejection
  // (aligned 2026-07-20; this previously resolved positive-wins, inverting the convention).
  for (const [key, opts] of Object.entries(rejected)) {
    const rejectedSet = new Set(opts);
    const kept = (traits[key] ?? []).filter((o) => !rejectedSet.has(o));
    if (kept.length) traits[key] = kept;
    else delete traits[key];
  }
  return {
    traits,
    rejected,
    likelihood,
    observations,
    error: null,
  };
}
